/**
 * NecklacePreview — founder-only comparison of three necklace shapes for
 * the hero band's brand entrance. Not linked from anywhere public.
 *
 * Route: /demo/necklace-preview
 *
 *   1. WAVY (current live)  — pearls float along an S-curve
 *   2. HANGING              — chain drapes with center bead at lowest point
 *   3. HANGING + CHARM      — hanging drape + stacked wordmark pendant
 *
 * Each section has its own key so hitting "Replay all" cleanly resets
 * every entrance animation from t=0.
 */

import { useState } from "react";
import { HeroBrandEntrance } from "../components/HeroBrandEntrance";
import { useNoIndex } from "../lib/useNoIndex";

const VARIANTS = [
  {
    key: "wavy",
    label: "Currently live",
    title: "Wavy chain (current).",
    blurb:
      "The chain drifts on an S-curve; each pearl sits at a different height. This is what the production hero shows today.",
  },
  {
    key: "hanging",
    label: "Concept A",
    title: "Chain hangs like a necklace.",
    blurb:
      "Chain drapes in a symmetric catenary curve so the lowest point sits at the horizontal center — reads as a real necklace at rest, not floating lights.",
  },
  {
    key: "hanging-charm",
    label: "Concept B",
    title: "Hanging + charm pendant.",
    blurb:
      "Same drape as concept A, with 5 palette beads split 3-and-2 around a black/gunmetal stacked-wordmark charm that dangles from a small silver bail at the center. Sequence: bead 1 → 2 → 3 → charm slides down and settles → 4 → 5.",
  },
];

export default function NecklacePreview() {
  useNoIndex();
  const [replayNonce, setReplayNonce] = useState(0);

  return (
    <main
      data-testid="necklace-preview-page"
      className="relative min-h-screen bg-sand px-6 py-14 text-ink lg:px-12 lg:py-20"
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-olive">
            Necklace concepts
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-tight text-ink lg:text-5xl">
            Three shapes for the brand entrance.
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink/70">
            All three reuse the same 5 palette gems and animation timing.
            The only variables are the chain path and whether a charm dangles
            from the center bead.
          </p>
          <button
            type="button"
            onClick={() => setReplayNonce((n) => n + 1)}
            data-testid="preview-replay-all"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/25 bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition hover:bg-beige"
          >
            Replay all animations
          </button>
        </header>

        {VARIANTS.map((v) => (
          <section
            key={v.key}
            id={`necklace-${v.key}`}
            data-testid={`necklace-preview-section-${v.key}`}
            className="mb-16 scroll-mt-8"
          >
            <div className="mb-4 flex items-baseline gap-3">
              <span className="rounded-full bg-yellow px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-ink">
                {v.label}
              </span>
              <h2 className="font-serif text-2xl text-ink lg:text-3xl">
                {v.title}
              </h2>
            </div>
            <p className="mb-6 max-w-3xl text-[13.5px] leading-relaxed text-ink/60">
              {v.blurb}
            </p>
            <div
              key={`${v.key}-${replayNonce}`}
              className="rounded-3xl border border-ink/10 bg-bone/60 px-4 py-10 lg:px-8 lg:py-14"
            >
              <HeroBrandEntrance
                autoPlay
                className="mt-0"
                necklaceShape={v.key}
              />
            </div>
          </section>
        ))}

        <p className="mt-12 text-center text-[12px] italic text-olive">
          Preview only · not linked from anywhere public · once you pick a
          winner it swaps into the production hero.
        </p>
      </div>
    </main>
  );
}
